import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAdmin } from "./AdminLayout";
import { useAdminLang, useLabel } from "./adminLang";
import { Field } from "./fieldkit";
import { GripVertical } from "lucide-react";

const DEFAULT_ORDER = ["credibility", "mission", "solutionsHome", "network", "segments", "rural", "why", "ecosystem"];

const HOME = [
  { key: "hero", label: "Héro", fields: [
    { key: "eyebrow", label: "Sur-titre", type: "text" },
    { key: "title1", label: "Titre — ligne 1", type: "text" },
    { key: "title2", label: "Titre — ligne 2 (accent)", type: "text" },
    { key: "sub", label: "Sous-titre", type: "textarea" },
    { key: "strip", label: "Puces services (une par ligne)", type: "list" },
  ] },
  { key: "credibility", label: "Bandeau crédibilité", fields: [
    { key: "items", label: "Éléments", type: "objlist", sub: [{ key: "title", label: "Titre" }, { key: "sub", label: "Sous-texte" }] },
  ] },
  { key: "mission", label: "Mission", fields: [
    { key: "heading", label: "Titre", type: "text" }, { key: "copy", label: "Texte", type: "textarea" },
    { key: "highlight", label: "Encadré", type: "textarea" }, { key: "cta", label: "Bouton", type: "text" },
  ] },
  { key: "solutionsHome", label: "Section Solutions", fields: [
    { key: "heading", label: "Titre", type: "text" }, { key: "intro", label: "Intro", type: "textarea" }, { key: "cta", label: "Bouton", type: "text" },
  ] },
  { key: "network", label: "Section Infrastructure", fields: [
    { key: "heading", label: "Titre", type: "text" }, { key: "copy", label: "Texte", type: "textarea" },
    { key: "highlights", label: "Points (un par ligne)", type: "list" },
    { key: "mapLabel", label: "Légende de la carte", type: "text" }, { key: "cta", label: "Bouton", type: "text" },
  ] },
  { key: "segments", label: "Section Secteurs", fields: [{ key: "heading", label: "Titre", type: "text" }] },
  { key: "rural", label: "Section Connectivité rurale", fields: [
    { key: "heading", label: "Titre", type: "text" }, { key: "copy", label: "Texte", type: "textarea" },
    { key: "support", label: "Phrase d'accent", type: "textarea" }, { key: "cta", label: "Bouton", type: "text" },
  ] },
  { key: "why", label: "Pourquoi Bula", fields: [
    { key: "heading", label: "Titre", type: "text" },
    { key: "items", label: "Éléments", type: "objlist", sub: [{ key: "title", label: "Titre" }, { key: "copy", label: "Texte" }] },
  ] },
  { key: "ecosystem", label: "Écosystème", fields: [{ key: "label", label: "Libellé", type: "text" }] },
  { key: "finalCta", label: "CTA final", fields: [{ key: "heading", label: "Titre", type: "text" }, { key: "copy", label: "Texte", type: "textarea" }] },
];

const HomeEditor = () => {
  const { canWrite } = useAdmin();
  const { tr } = useAdminLang();
  const L = useLabel();
  const [data, setData] = useState(null); // { fr:{...}, en:{...} }
  const [locale, setLocale] = useState("fr");
  const [saved, setSaved] = useState(false);
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [dragIdx, setDragIdx] = useState(null);
  const [orderSaved, setOrderSaved] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setData(r.data.home_content || { fr: {}, en: {} });
      const o = r.data.home_order;
      setOrder(Array.isArray(o) && o.length ? o : DEFAULT_ORDER);
    }).catch(() => {});
  }, []);

  const LABELS = Object.fromEntries(HOME.map((s) => [s.key, s.label]));
  const move = (from, to) => {
    if (from === null || to === null || from === to) return;
    const a = [...order]; const [x] = a.splice(from, 1); a.splice(to, 0, x); setOrder(a);
  };
  const saveOrder = async () => {
    try {
      await api.put("/admin/settings/home_order", order);
      setOrderSaved(true); setTimeout(() => setOrderSaved(false), 2000);
    } catch { setOrderSaved(false); }
  };

  if (!data) return <p className="text-white/40 text-sm">Chargement…</p>;

  const cur = data[locale] || {};
  const setSection = (sectionKey, fieldKey, value) =>
    setData((prev) => ({
      ...prev,
      [locale]: { ...prev[locale], [sectionKey]: { ...(prev[locale]?.[sectionKey] || {}), [fieldKey]: value } },
    }));

  const save = async () => {
    await api.put("/admin/settings/home_content", data);
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div data-testid="admin-home-editor">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-black text-white">{tr("Page d'accueil", "Home page")}</h1>
        <div className="flex gap-2" data-testid="home-editor-locale-tabs">
          {["fr", "en"].map((lc) => (
            <button key={lc} onClick={() => setLocale(lc)} data-testid={`home-editor-tab-${lc}`}
              className={`text-xs font-black rounded-full px-4 py-1.5 transition-colors ${locale === lc ? "bg-[#FF6600] text-[#08090A]" : "bg-white/5 text-white/60 hover:text-white"}`}>
              {lc.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 space-y-6 max-w-3xl">
        <section className="bg-[#101214] border border-white/10 rounded-2xl p-6" data-testid="home-order-card">
          <h2 className="font-bold text-white text-sm">{tr("Ordre des sections (glisser-déposer)", "Section order (drag & drop)")}</h2>
          <p className="mt-1 text-[11px] text-white/40">{tr("Le héro reste en premier et le CTA final en dernier. Réordonnez les blocs intermédiaires.", "The hero stays first and the final CTA last. Reorder the middle blocks.")}</p>
          <ul className="mt-4 space-y-2">
            {order.map((k, i) => (
              <li key={k} draggable data-testid={`home-order-item-${k}`}
                onDragStart={() => setDragIdx(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { move(dragIdx, i); setDragIdx(null); }}
                onDragEnd={() => setDragIdx(null)}
                className={`flex items-center gap-2.5 bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 cursor-move text-sm text-white/80 ${dragIdx === i ? "opacity-50" : ""}`}>
                <GripVertical size={16} className="text-white/40" />
                <span className="font-mono text-[#FF9900] text-xs">{i + 1}</span>
                {L(LABELS[k] || k)}
              </li>
            ))}
          </ul>
          {canWrite && (
            <button onClick={saveOrder} data-testid="home-order-save"
              className="mt-4 bg-white/10 hover:bg-[#FF6600] hover:text-[#08090A] text-white text-xs font-black rounded-full px-6 py-2.5 transition-colors">
              {orderSaved ? tr("Ordre enregistré ✓", "Order saved ✓") : tr("Enregistrer l'ordre", "Save order")}
            </button>
          )}
        </section>

        {HOME.map((section) => (
          <section key={section.key} className="bg-[#101214] border border-white/10 rounded-2xl p-6" data-testid={`home-section-${section.key}`}>
            <h2 className="font-bold text-white text-sm mb-4">{L(section.label)}</h2>
            <div className="space-y-4">
              {section.fields.map((f) => (
                <Field key={f.key} field={f} value={cur[section.key]?.[f.key]} onChange={(v) => setSection(section.key, f.key, v)} />
              ))}
            </div>
          </section>
        ))}
      </div>

      {canWrite && (
        <button onClick={save} data-testid="home-editor-save"
          className="fixed bottom-6 right-6 bg-[#FF6600] hover:bg-[#FF9900] text-[#08090A] text-sm font-black rounded-full px-7 py-3.5 shadow-xl transition-colors">
          {saved ? tr("Enregistré ✓", "Saved ✓") : tr("Enregistrer", "Save")}
        </button>
      )}
    </div>
  );
};

export default HomeEditor;
